import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
// Location uses native select (no react-select)
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaFileExport, 
  FaSearch,
  FaDownload,
  FaArrowRight,
  FaArrowLeft,
  FaBox,
  FaWarehouse,
  FaTruck,
  FaExpand,
  FaCompress,
  FaBook,
  FaTrashAlt
} from 'react-icons/fa';

export default function StockItemsDayBook() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [dayBookData, setDayBookData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalEntries: 0,
    entriesPerPage: 50
  });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    location: '',
    page: 1,
    limit: 50,
    sortBy: 'date',
    sortOrder: 'asc'
  });



  // Fullscreen
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const syncDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(syncDarkMode, 500);
    return () => clearInterval(interval);
  }, []);



  // Get company ID from user context
  const getCompanyId = () => {
      return user?.user?.company?._id || user?.user?.company;
    return '';
  };

  useEffect(() => {
    fetchLocations();
    // Set default date range to last 3 days (like Tiles Day Book)
    const now = new Date();
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(now.getDate() - 3);
    
    setFilters(prev => ({
      ...prev,
      dateFrom: threeDaysAgo.toISOString().split('T')[0],
      dateTo: now.toISOString().split('T')[0]
    }));
  }, []);

  useEffect(() => {
    // Auto-fetch data when filters change (dates are required, location is optional)
    if (filters.dateFrom && filters.dateTo) {
      fetchDayBookData();
    }
  }, [filters.dateFrom, filters.dateTo, filters.location, filters.limit, filters.sortBy, filters.sortOrder, filters.page]);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchDayBookData = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire('Warning', 'Please select date range', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        page: filters.page.toString(),
        limit: filters.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        companyId: getCompanyId()
      });

      // Add location only if selected
      if (filters.location) {
        queryParams.append('location', filters.location);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/stock-items/day-book?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stock Items Day Book Response:', data);
        setDayBookData(data.entries || []);
        setPagination({
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 0,
          totalEntries: data.totalEntries || 0,
          entriesPerPage: data.entriesPerPage || 50
        });
        console.log('Pagination set to:', {
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 0,
          totalEntries: data.totalEntries || 0,
          entriesPerPage: data.entriesPerPage || 50
        });
      } else {
        Swal.fire('Error', 'Failed to fetch day book data', 'error');
      }
    } catch (error) {
      console.error('Error fetching day book data:', error);
      Swal.fire('Error', 'Error occurred while fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field !== 'page' && { page: 1 }) // Only reset to first page when filter changes, not when page changes
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleExport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire('Warning', 'Please select date range before exporting', 'warning');
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        companyId: getCompanyId(),
        export: 'true'
      });

      // Add location only if selected
      if (filters.location) {
        queryParams.append('location', filters.location);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/stock-items/day-book/export?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `stock-items-day-book-${filters.dateFrom}-to-${filters.dateTo}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'Day book exported successfully', 'success');
      } else {
        Swal.fire('Error', 'Failed to export day book', 'error');
      }
    } catch (error) {
      console.error('Error exporting day book:', error);
      Swal.fire('Error', 'Error occurred while exporting', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-IN');
  };

  const getTransactionTypeStyle = (type) => {
    if (type === 'INWARD') return darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-800';
    if (type === 'TRANSFER IN') return darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-800';
    if (type === 'DESTROYED') return darkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-800';
    return darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-800';
  };

  const getTransactionIcon = (type) => {
    if (type === 'DESTROYED') return <FaTrashAlt className="mr-1" />;
    return (type === 'INWARD' || type === 'TRANSFER IN') ? 
      <FaArrowRight className="mr-1" /> : 
      <FaArrowLeft className="mr-1" />;
  };

  const groupEntriesByDate = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const dateKey = new Date(entry.date).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(entry);
    });
    
    return grouped;
  };

  const groupedData = groupEntriesByDate(dayBookData);

  return (
    <div ref={containerRef} className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'} ${isFullscreen ? 'overflow-auto' : ''}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className={`rounded-2xl shadow-2xl border-0 mb-6 overflow-hidden transition-colors duration-300`}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <FaBook className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-white mb-2">Stock Items Day Book</h1>
                  <p className="text-white/90">Daily stock items transactions report</p>
                </div>
              </div>
              <button onClick={toggleFullscreen} className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200 backdrop-blur-sm" title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={`p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2 text-indigo-500" />
                  Date From *
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-4 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-indigo-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2 text-indigo-500" />
                  Date To *
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-4 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-indigo-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-2 text-indigo-500" />
                  Location
                </label>
                <select
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className={`w-full px-4 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-indigo-300 bg-white'}`}
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name || loc.locationName}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchDayBookData}
                  disabled={loading || !filters.dateFrom || !filters.dateTo}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Export Options */}
            {dayBookData.length > 0 && (
              <div className={`mt-4 pt-4 border-t-2 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Export Options:</span>
                    <button
                      onClick={handleExport}
                      disabled={loading}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1.5 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                    >
                      <FaFileExport />
                      Excel
                    </button>
                  </div>
                  
                  {/* Pagination Controls */}
                  {dayBookData.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Page {pagination.currentPage || 1} of {pagination.totalPages || 1} 
                        ({pagination.totalEntries || dayBookData.length} total entries)
                      </span>
                      {pagination.totalPages > 1 && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage <= 1 || loading}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${darkMode ? 'bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:text-gray-500' : 'bg-gray-100 hover:bg-indigo-100 hover:text-indigo-600 disabled:bg-gray-50 disabled:text-gray-300'}`}
                          >
                            <FaArrowLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage >= pagination.totalPages || loading}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${darkMode ? 'bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:text-gray-500' : 'bg-gray-100 hover:bg-indigo-100 hover:text-indigo-600 disabled:bg-gray-50 disabled:text-gray-300'}`}
                          >
                            <FaArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page Size Selector */}
        {dayBookData.length > 0 && (
          <div className={`rounded-2xl shadow-xl border-2 mb-6 transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Entries per page:</label>
                <select
                  value={filters.limit}
                  onChange={(e) => {
                    handleFilterChange('limit', parseInt(e.target.value));
                  }}
                  className={`px-3 py-1.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-indigo-300'}`}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              
              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sort by:</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => {
                    handleFilterChange('sortBy', e.target.value);
                  }}
                  className={`px-3 py-1.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-indigo-300'}`}
                >
                  <option value="date">Date</option>
                  <option value="itemName">Item Name</option>
                  <option value="type">Transaction Type</option>
                </select>
                <button
                  onClick={() => {
                    const newOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc';
                    handleFilterChange('sortOrder', newOrder);
                  }}
                  className={`px-3 py-1.5 border-2 rounded-xl text-sm flex items-center gap-1 transition-all duration-200 font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600'}`}
                >
                  {filters.sortOrder === 'asc' ? '↑' : '↓'} 
                  {filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {/* Results */}
        {Object.keys(groupedData).length > 0 ? (
          <div className={`rounded-2xl shadow-xl border-2 overflow-hidden transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <div className={`px-6 py-5 border-b-2 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gradient-to-r from-gray-50 to-white'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Day Book Report ({formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)})
                </h2>
                <span className={`text-sm font-medium px-3 py-1 rounded-full ${darkMode ? 'text-gray-300 bg-gray-700' : 'text-indigo-700 bg-indigo-50'}`}>
                  Total Entries: {dayBookData.length}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {Object.entries(groupedData)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([dateKey, entries]) => (
                <div key={dateKey} className={`border-b last:border-b-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  {/* Date Header */}
                  <div className={`px-6 py-3 border-b-2 ${darkMode ? 'bg-indigo-900/20 border-gray-700' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100'}`}>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-800'}`}>
                      {formatDate(dateKey)}
                    </h3>
                  </div>

                  {/* Entries for this date */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            Item Details
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            Location
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            Transaction Type
                          </th>
                          <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'bg-green-900/30 text-gray-300' : 'bg-green-50 text-gray-500'}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowRight className={`${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                              Inward
                            </div>
                          </th>
                          <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'bg-red-900/30 text-gray-300' : 'bg-red-50 text-gray-500'}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowLeft className={`${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                              Outward
                            </div>
                          </th>
                          <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            Balance
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            Reference
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            DO Number
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {entries.map((entry, index) => (
                          <tr key={`${dateKey}_${index}`} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}>
                            <td className="px-4 py-3">
                              <div>
                                <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry.itemName}</div>
                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{entry.itemId || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FaMapMarkerAlt className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                                <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry.locationName}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeStyle(entry.type)}`}>
                                {getTransactionIcon(entry.type)}
                                {entry.type}
                              </span>
                            </td>
                            <td className={`px-4 py-3 text-center ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                              {(entry.type === 'INWARD' || entry.type === 'TRANSFER IN') ? (
                                <div className={`font-medium text-lg ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                                  +{entry.quantity}
                                </div>
                              ) : (
                                <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-center ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                              {(entry.type === 'OUTWARD' || entry.type === 'TRANSFER OUT' || entry.type === 'DESTROYED') ? (
                                <div className={`font-medium text-lg ${entry.type === 'DESTROYED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>
                                  -{entry.quantity}
                                </div>
                              ) : (
                                <div className={`${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className={`font-medium text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {entry.balance}
                              </div>
                            </td>
                            <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {entry.reference}
                            </td>
                            <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {entry.deliveryOrderNumber || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl shadow-xl border-2 transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <div className={`px-6 py-16 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {loading ? (
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div>
                  <FaBox className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No transactions found</h3>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {!filters.dateFrom || !filters.dateTo ? 
                      'Please select date range to view data' : 
                      'No data available for the selected criteria'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
